import {useEffect, useRef, useState} from "react";
import {useAuthsState,} from "../auths/authsContext";
import {useMyMuseums} from "../museum/useMyMuseums";
import {useDmsStatusState} from "./DmsStatusContext";
import {kickOffDmsStatusWorker} from "../webworkers/kickOffDmsStatusWorker";
import {clientLog} from "../clientLog";
import {useDocumentVisibility} from "../useDocumentVisibility";
import {stopDmsStatusWorker} from "../webworkers/stopDmsStatusWorker";
import {useDocumentTranslation} from "../documents/documentContext";
import {useSnackbar} from "notistack";

/**
 * Displays a warning or error message if DAMS storage is unavailable.
 * If a museum doesn't have a DAMS storage config, a warning message is shown.
 * If the entire DAMS storage is unavailable, an error message is shown.
 *
 * @returns {JSX.Element} A message to be rendered.
 */
export const DmsStatus = () => {

    const {enqueueSnackbar} = useSnackbar();

    const componentName = 'DmsStatus';

    const t = useDocumentTranslation();

    const {userIsAuthenticated} = useAuthsState();
    const {dmsStatus} = useDmsStatusState();

    const myMuseums = useMyMuseums({});

    const isDocumentVisible = useDocumentVisibility();

    const [dmsStatusDisplayed, setDmsStatusDisplayed] = useState({
        warning: false,
        errors: false,
    });

    // Ref that holds any pending stopDmsStatusWorker timeout so it can be
    // cancelled before it fires when visibility or auth state changes.
    const stopTimeoutRef = useRef(null);

    /**
     * Displays an error message if DAMS storage is unavailable.
     */
    const showError = () => {
        enqueueSnackbar(t(
            "snackbarDamsErrorMessage",
            "DAMS lagringsområde ikke tilgjengelig - kontakt support."
        ), {variant: 'error'});

        // Functional updater avoids capturing a stale dmsStatusDisplayed snapshot.
        setDmsStatusDisplayed(prev => ({...prev, errors: true}));
    };

    /**
     * Displays a warning message if DAMS storage is unavailable for some museums.
     * @param {array} museumIdentifiers
     */
    const showWarning = (museumIdentifiers) => {
        const message =
            t(
                "damsWarningMessage",
                "DAMS lagringsområde kan ikke nås for disse museene: "
            ) +
            museumIdentifiers?.map((m) => getMuseumNameFromIdentifier(m)).join(", ");

        enqueueSnackbar(message, {variant: 'warning'});

        // Functional updater avoids capturing a stale dmsStatusDisplayed snapshot.
        setDmsStatusDisplayed(prev => ({...prev, warning: true}));
    };

    /**
     * Finds the name of the museum with the specified identifier.
     * @param {string} identifier The identifier of the museum to find.
     * @returns {string|null} The name of the museum, or null if it could not be found.
     */
    const getMuseumNameFromIdentifier = (identifier) => {
        if (!myMuseums || myMuseums.length === 0) {
            return;
        }
        return myMuseums.find((m) => m['ekultur_id'] === identifier)?.name;
    };

    /**
     * Hook used to start/stop the DMSStatus web-worker.
     * Stores the pending stop-timeout in a ref so it can be cancelled if the
     * component re-renders or unmounts before the 10 s delay elapses.
     */
    useEffect(() => {
        if (userIsAuthenticated && myMuseums.length > 0) {
            if (isDocumentVisible) {
                // Tab became visible — cancel any queued stop and (re)start the worker.
                if (stopTimeoutRef.current !== null) {
                    clearTimeout(stopTimeoutRef.current);
                    stopTimeoutRef.current = null;
                }
                kickOffDmsStatusWorker();
            } else {
                stopTimeoutRef.current = setTimeout(() => {
                    stopDmsStatusWorker();
                    stopTimeoutRef.current = null;
                }, 10000);
            }
        }
        return () => {
            if (stopTimeoutRef.current !== null) {
                clearTimeout(stopTimeoutRef.current);
                stopTimeoutRef.current = null;
            }
        };
    }, [userIsAuthenticated, myMuseums, isDocumentVisible]);

    useEffect(() => {
        if (!userIsAuthenticated || !myMuseums || myMuseums.length === 0) {
            return;
        }

        // If requesting configs. fails, log the error and alert the user.
        if (dmsStatus.errorCount > 0 && !dmsStatusDisplayed.errors) {
            dmsStatus.errors.forEach(error => {
                clientLog('error', error, componentName);
            });
            clientLog('error', 'DAMS storage is unavailable - contact support', componentName);
            showError();
        }

        // If missing museum configs. nag user and log to console.
        if (dmsStatus['missingConfigs']?.length > 0 && !dmsStatusDisplayed.warning) {
            for (let i = 0, max = dmsStatus['missingConfigs'].length; i < max; i++) {
                clientLog('warn',
                    'missing config for: ' + dmsStatus['missingConfigs'][i],
                    componentName);
            }
            showWarning(dmsStatus['missingConfigs']);
        }
    }, [userIsAuthenticated, myMuseums, dmsStatus, dmsStatusDisplayed]); // eslint-disable-line react-hooks/exhaustive-deps
};
